import { type ComponentPropsWithoutRef, type ReactNode } from "react";

import { cx } from "class-variance-authority";

import * as theme from "./EmptyState.css";

export type EmptyStateTone = keyof typeof theme.chipTone;

// `children` would be silently dropped and `title` is our own text, not the
// native tooltip attribute.
export interface EmptyStateProps extends Omit<
    ComponentPropsWithoutRef<"div">,
    "children" | "title"
> {
    title: string;
    description?: string;
    /** Icon for the round chip. Omitted means no chip at all. */
    icon?: ReactNode;
    /** Palette ramp for the chip: shade 50 fills it, shade 500 colours the icon. */
    tone?: EmptyStateTone;
}

/**
 * Empty state component - shown for widgets, lists or similar that have no data to show.
 */
export function EmptyState({
    title,
    description,
    icon,
    tone = "lightBlue",
    className,
    ...props
}: EmptyStateProps) {
    return (
        <div className={cx(theme.root, className)} {...props}>
            {icon && (
                <span className={cx(theme.iconChip, theme.chipTone[tone])}>
                    <span className={theme.icon}>{icon}</span>
                </span>
            )}
            <span className={theme.copy}>
                <span className={theme.title}>{title}</span>
                {description && (
                    <span className={theme.description}>{description}</span>
                )}
            </span>
        </div>
    );
}
